import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { SESSION } from "../constants/testIds";
import { QuestionContent, ChoiceContent } from "../components/QuestionContent";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

/**
 * UX1.2 practice flow:
 *  - Tap a choice → selects it (and saves immediately).
 *  - Tap the SAME choice again → deselects; the question returns to
 *    UNANSWERED. NO blank-reason prompt is shown during the session.
 *  - There is NO separate "Boş Bırak" button. A question with no selected
 *    choice is simply left blank.
 *  - "Sonraki" always works, even with nothing selected.
 *  - "Bitir" always works; if questions are still unanswered the button
 *    becomes a two-tap confirm ("N soru boş — Yine de Bitir") to avoid
 *    accidental completion on mobile.
 *  - Blank reasons are asked ONLY after completion, in one batch, on the
 *    result screen (SessionResult).
 */
export default function Session() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  // answers: question_id -> "A"/"B"/... (only ANSWERED questions appear here)
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [t0, setT0] = useState(Date.now());

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/sessions/${id}`);
      setSession(data);
      // Resume support: rehydrate previously saved answers so students
      // don't re-answer questions they already completed in a prior visit.
      try {
        const rv = await api.get(`/sessions/${id}/review`);
        const prior = {};
        (rv.data?.items || []).forEach((it) => {
          if (it.selected) prior[it.question_id] = it.selected;
        });
        setAnswers(prior);
        const firstUnanswered = (data.questions || []).findIndex(
          (qq) => !(qq.question_id in prior),
        );
        if (firstUnanswered > 0) setIdx(firstUnanswered);
      } catch { /* review may 404 for brand-new; ignore */ }
      setT0(Date.now());
    })();
  }, [id]);

  const q = session?.questions?.[idx];
  const total = session?.questions?.length || 10;
  const answeredCount = Object.keys(answers).length;
  const unanswered = total - answeredCount;
  const progress = useMemo(() => Math.round((answeredCount / total) * 100), [answeredCount, total]);

  const pick = async (key) => {
    if (!q) return;
    const current = answers[q.question_id];
    if (current === key) {
      // Deselect → question goes back to unanswered. No modal, no prompt.
      setAnswers((a) => {
        const cp = { ...a }; delete cp[q.question_id]; return cp;
      });
      try { await api.delete(`/sessions/${id}/answer/${q.question_id}`); } catch { /* silent */ }
      return;
    }
    setAnswers((a) => ({ ...a, [q.question_id]: key }));
    try {
      await api.post(`/sessions/${id}/answer`, {
        question_id: q.question_id,
        selected: key,
        time_ms: Date.now() - t0,
      });
    } catch { /* silent — persistence is best-effort */ }
  };

  const next = () => {
    setConfirmFinish(false);
    if (idx < total - 1) { setIdx(idx + 1); setT0(Date.now()); }
  };
  const prev = () => {
    setConfirmFinish(false);
    if (idx > 0) { setIdx(idx - 1); setT0(Date.now()); }
  };

  const finish = async () => {
    // Two-tap confirm only when unanswered questions remain.
    if (unanswered > 0 && !confirmFinish) {
      setConfirmFinish(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/sessions/${id}/complete`);
      nav(`/sonuc/${id}`);
    } catch { toast.error("Oturum tamamlanamadı"); }
    finally { setBusy(false); }
  };

  if (!session || !q) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 bg-[#F8FAFC]">Yükleniyor…</div>;
  }

  const answered = answers[q.question_id];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <button onClick={() => nav("/")} className="text-slate-500 hover:text-[#0B1A35] transition-colors">Çıkış</button>
            <div className="font-mono text-slate-500">Soru {idx + 1} / {total}</div>
            <div className="font-mono text-xs text-slate-400">{q.ref}</div>
          </div>
          <div data-testid={SESSION.progress} className="h-1.5 mt-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FFD54F] to-[#FFC107] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-6 pb-40">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-6 overflow-hidden">
          <div data-testid={SESSION.questionText} className="overflow-x-auto">
            <QuestionContent question={q} />
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          {q.choices.map((c) => {
            const selected = answered === c.key;
            return (
              <button key={c.key} onClick={() => pick(c.key)} data-testid={SESSION.choice(c.key)}
                className={`w-full text-left rounded-xl border p-4 transition-all flex items-start gap-3 min-h-[56px] ${
                  selected ? "border-[#007BFF] bg-[#EAF3FF] ring-1 ring-[#007BFF]"
                           : "border-[#E2E8F0] bg-white hover:border-[#0B1A35]"
                }`}>
                <span className={`shrink-0 w-8 h-8 rounded-full grid place-items-center font-mono text-sm ${
                  selected ? "bg-[#007BFF] text-white" : "bg-slate-100 text-slate-700"
                }`}>{c.key}</span>
                <ChoiceContent choice={c} />
                {selected && <Check size={18} className="text-[#007BFF]"/>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[#E2E8F0] bg-white/95 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto p-3 flex items-center justify-between">
          <button onClick={prev} disabled={idx === 0} data-testid={SESSION.prev}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:opacity-40">
            <ArrowLeft size={16}/> Önceki
          </button>
          {idx < total - 1 ? (
            <button onClick={next} data-testid={SESSION.next}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#0B1A35] text-white hover:bg-[#16294D] transition-colors">
              Sonraki <ArrowRight size={16}/>
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid={SESSION.finish}
              className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg disabled:opacity-60 ${
                confirmFinish ? "bg-rose-600 text-white" : "yz-cta"
              }`}>
              {busy ? "Tamamlanıyor…"
                : confirmFinish && unanswered > 0 ? `${unanswered} soru boş — Yine de Bitir`
                : "Bitir"} <Check size={16}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
